import axios from 'axios';

// Defaults to a same-origin relative path so the app works when served
// directly by the backend (e.g. Railway); override via env var when the
// frontend is hosted separately from the API.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Include cookies for auth
});

// Auth APIs
export const adminLogin = async (username, password) => {
  const response = await api.post('/admin/login', { username, password });
  return response.data;
};

export const adminLogout = async () => {
  const response = await api.post('/admin/logout');
  return response.data;
};

export const getAdminMe = async () => {
  const response = await api.get('/admin/me');
  return response.data;
};

// Subscriber APIs
export const subscribeEmail = async (email, frequency) => {
  const response = await api.post('/subscribers', { email, frequency });
  return response.data;
};

export const confirmSubscription = async (token) => {
  const response = await api.get(`/subscribers/confirm?token=${token}`);
  return response.data;
};

export const unsubscribe = async (token) => {
  const response = await api.get(`/subscribers/unsubscribe?token=${token}`);
  return response.data;
};

// Frequency management APIs
export const getSubscriberFrequency = async (token) => {
  const response = await api.get(`/subscribers/frequency?token=${token}`);
  return response.data;
};

export const updateSubscriberFrequency = async (token, frequency) => {
  const response = await api.post('/subscribers/frequency', { token, frequency });
  return response.data;
};

// Admin APIs
export const getAdminStats = async () => {
  const response = await api.get('/admin/stats');
  return response.data;
};

export const getSources = async () => {
  const response = await api.get('/admin/sources');
  return response.data;
};

export const toggleSource = async (sourceName, enabled) => {
  const response = await api.post('/admin/sources/toggle', { 
    source_name: sourceName, 
    enabled 
  });
  return response.data;
};

export const runDigestNow = async (testMode = true) => {
  const response = await api.post('/admin/run-now', { test_mode: testMode });
  return response.data;
};

export const getLastEmail = async () => {
  const response = await api.get('/admin/last-email');
  return response.data;
};

export const getSettings = async () => {
  const response = await api.get('/admin/settings');
  return response.data;
};

export const updateSettings = async (settings) => {
  const response = await api.put('/admin/settings', settings);
  return response.data;
};

export const toggleSandboxMode = async (enabled) => {
  const response = await api.post(`/admin/sandbox-toggle?enabled=${enabled}`);
  return response.data;
};

// Announcement banner APIs (independent per-frequency text shown at the top
// of the newsletter, above Today's Highlights)
export const getAnnouncementBanners = async () => {
  const response = await api.get('/admin/announcement-banners');
  return response.data;
};

export const setAnnouncementBanner = async (frequency, message) => {
  const response = await api.put(`/admin/announcement-banners/${frequency}`, { message });
  return response.data;
};

export const clearAnnouncementBanner = async (frequency) => {
  const response = await api.delete(`/admin/announcement-banners/${frequency}`);
  return response.data;
};

export const sendTestEmail = async (email) => {
  const response = await api.post('/admin/send-test-email', { email });
  return response.data;
};

export const sendTestDigest = async (email, frequency, mode = 'scheduled') => {
  const response = await api.post('/admin/test-digest', { email, frequency, mode });
  return response.data;
};

export const runScheduledDigestNow = async (targetEmail, frequency) => {
  const response = await api.post('/admin/run-digest-now', { 
    target_email: targetEmail, 
    frequency 
  });
  return response.data;
};

export const getDigestJobStatus = async (jobId) => {
  const response = await api.get(`/admin/digest-job-status/${jobId}`);
  return response.data;
};

// Subscriber management
export const getSubscribers = async () => {
  const response = await api.get('/admin/subscribers');
  return response.data;
};

export const deleteSubscriber = async (token) => {
  const response = await api.delete(`/admin/subscribers/${token}`);
  return response.data;
};

export const adminConfirmSubscriber = async (token) => {
  const response = await api.post(`/admin/subscribers/${token}/confirm`);
  return response.data;
};

export const addSubscriber = async (email, frequency, note) => {
  const response = await api.post('/admin/subscribers', { email, frequency, note });
  return response.data;
};

// Feed health and scrape results
export const getFeedHealth = async () => {
  const response = await api.get('/admin/feed-health');
  return response.data;
};

export const getLatestRunHistory = async () => {
  const response = await api.get('/admin/run-history/latest');
  return response.data;
};

// Lookback windows
export const getLookbackWindows = async () => {
  const response = await api.get('/admin/lookback-windows');
  return response.data;
};

// Quality Assurance APIs
export const getQALogs = async (limit = 10) => {
  const response = await api.get(`/admin/qa/logs?limit=${limit}`);
  return response.data;
};

export const getQALogDetail = async (runId) => {
  const response = await api.get(`/admin/qa/log/${runId}`);
  return response.data;
};

export const getPendingReview = async () => {
  const response = await api.get('/admin/qa/pending-review');
  return response.data;
};

export const approveEmail = async (runId) => {
  const response = await api.post(`/admin/qa/approve/${runId}`);
  return response.data;
};

export const getQAStatistics = async () => {
  const response = await api.get('/admin/qa/statistics');
  return response.data;
};

export const getReviewBeforeSendSetting = async () => {
  const response = await api.get('/admin/settings/review-before-send');
  return response.data;
};

export const setReviewBeforeSendSetting = async (enabled) => {
  const response = await api.post(`/admin/settings/review-before-send?enabled=${enabled}`);
  return response.data;
};

// Source Coverage Report
export const getSourceCoverage = async (startDate = null, endDate = null) => {
  const params = new URLSearchParams();
  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const queryString = params.toString() ? `?${params.toString()}` : '';
  const response = await api.get(`/admin/source-coverage${queryString}`);
  return response.data;
};

export const getSourceScrapeHistory = async (sourceName, limit = 20) => {
  const response = await api.get(`/admin/source-coverage/history/${encodeURIComponent(sourceName)}?limit=${limit}`);
  return response.data;
};

// SerpAPI Usage
export const getSerpApiUsage = async () => {
  const response = await api.get('/admin/serpapi-usage');
  return response.data;
};

export const cleanupStaleJobs = async () => {
  const response = await api.post('/admin/cleanup-stale-jobs');
  return response.data;
};

export const getRunHistory = async (limit = 50) => {
  const response = await api.get(`/admin/run-history?limit=${limit}`);
  return response.data;
};

export const getRunDetails = async (runId) => {
  const response = await api.get(`/admin/run-history/${runId}`);
  return response.data;
};

export const getCanonicalStoreStats = async () => {
  const response = await api.get('/admin/canonical-store');
  return response.data;
};
